import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Alert,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Switch,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Upload as UploadIcon, Delete as DeleteIcon, AutoFixHigh as AutoFixHighIcon, Lock as LockIcon, LockOpen as LockOpenIcon } from '@mui/icons-material';
import { usePptxFile } from '../hooks/usePptxFile';
import SlidePreviewWithBoundaries from '../components/pptxFiles/SlidePreviewWithBoundaries';
import { useAppContext } from '../contexts/AppContext';
import { useLang } from '../contexts/langContext';
import { processPromisesWithConcurrency } from '../utils/concurrency';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../hooks/useAuth';

function PptxFileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editingSlideIndices, setEditingSlideIndices] = useState<Set<number>>(new Set());
  const [editedDescriptions, setEditedDescriptions] = useState<{ [key: number]: string }>({});
  const [isGeneratingFileDescription, setIsGeneratingFileDescription] = useState(false);
  const [editingFileDescription, setEditingFileDescription] = useState(false);
  const [editedFileDescription, setEditedFileDescription] = useState('');
  const [generatingDescriptions, setGeneratingDescriptions] = useState<number[]>([]);
  const [uploadingThumbnails, setUploadingThumbnails] = useState<number[]>([]);
  const [generatedDescriptions, setGeneratedDescriptions] = useState<{ [key: number]: string }>({});
  const [lockedElements, setLockedElements] = useState<{ [key: string]: boolean }>({});
  const [hoveredElement, setHoveredElement] = useState<{ slideNumber: number; elemIndex: number } | null>(null);
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const { setAppTitle } = useAppContext();
  const { lang } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    setAppTitle(lang === 'en' ? 'PPTX Template Detail' : 'PPTX 模板详情');
  }, [lang, setAppTitle]);

  const {
    pptxFile: currentFile,
    loading: isLoading,
    error,
    handleUpdatePptxFile,
    handleUpdateSlideDescription,
    handleGenerateDescriptionFromSlide,
    handleUploadThumbnail,
    handleDeleteThumbnail,
    handleGeneratePptxDescription,
    handleUpdateSlideElementLocked,
  } = usePptxFile(id);

  // Check if user is not creator and file is public (read-only mode)
  const isReadOnlyMode = Boolean(currentFile && user && currentFile.public && currentFile.createdBy !== user._id);

  useEffect(() => {
    // Initialize locked elements
    if (currentFile) {
      const initialLocked: { [key: string]: boolean } = {};
      currentFile.parsedSlides.forEach((slide, slideIndex) => {
        slide.elements.forEach((element, elemIndex) => {
          const key = `${slideIndex + 1}-${elemIndex}`;
          initialLocked[key] = (element as any).isLocked || false;
        });
      });
      setLockedElements(initialLocked);
    }
  }, [currentFile]);

  const branding = useBranding();

  const handleEditDescription = useCallback((slideIndex: number, currentDescription: string) => {
    if (isReadOnlyMode) return;
    setEditingSlideIndices(prev => new Set(prev).add(slideIndex));
    setEditedDescriptions(prev => ({
      ...prev,
      [slideIndex]: currentDescription
    }));
  }, [isReadOnlyMode]);

  const handleSaveDescription = useCallback(async (slideIndex: number) => {
    if (isReadOnlyMode) return;
    try {
      if (!currentFile) return;
      const description = editedDescriptions[slideIndex];
      await handleUpdateSlideDescription(slideIndex, description);
      
      setEditingSlideIndices(prev => {
        const newSet = new Set(prev);
        newSet.delete(slideIndex);
        return newSet;
      });
      
      const { [slideIndex]: _, ...restDescriptions } = editedDescriptions;
      setEditedDescriptions(restDescriptions);
      
      const { [slideIndex]: __, ...restGenerated } = generatedDescriptions;
      setGeneratedDescriptions(restGenerated);
    } catch (err) {
      console.error('Failed to update description:', err);
    }
  }, [currentFile, editedDescriptions, handleUpdateSlideDescription, generatedDescriptions, isReadOnlyMode]);

  const handleCancelEdit = (slideIndex: number) => {
    setEditingSlideIndices(prev => {
      const newSet = new Set(prev);
      newSet.delete(slideIndex);
      return newSet;
    });
    setEditedDescriptions(prev => {
      const { [slideIndex]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleGenerateDescription = async (slideNumber: number) => {
    if (!currentFile || isReadOnlyMode) return;
    
    try {
      setGeneratingDescriptions(prev => [...prev, slideNumber]);
      const description = await handleGenerateDescriptionFromSlide(slideNumber, { lang });
      
      if (description) {
        setGeneratedDescriptions(prev => ({
          ...prev,
          [slideNumber]: description
        }));
        setEditingSlideIndices(prev => new Set(prev).add(slideNumber));
        setEditedDescriptions(prev => ({
          ...prev,
          [slideNumber]: description
        }));
      }
    } catch (err) {
      console.error('Failed to generate description:', err);
    } finally {
      setGeneratingDescriptions(prev => prev.filter(num => num !== slideNumber));
    }
  };

  const handleGenerateAllMissingDescriptions = async () => {
    if (!currentFile || isReadOnlyMode) return;
    
    const slidesWithoutDescription = currentFile.parsedSlides
      .filter(slide => !slide.description)
      .map(slide => slide.slideNumber);

    setGeneratingDescriptions(prev => [...prev, ...slidesWithoutDescription]);
    
    try {
      // Create an array of task functions
      const descriptionTasks = slidesWithoutDescription.map(slideNumber => 
        async () => {
          const description = await handleGenerateDescriptionFromSlide(slideNumber, { lang });
          return { slideNumber, description };
        }
      );
      
      // Create the generator with concurrency of 2
      const generator = processPromisesWithConcurrency(descriptionTasks, branding.limits.concurrentModelRequest, 
        (error, taskIndex) => {
          console.error(`Error generating description for slide ${slidesWithoutDescription[taskIndex]}:`, error);
          // Remove this slide from generating state
          setGeneratingDescriptions(prev => 
            prev.filter(num => num !== slidesWithoutDescription[taskIndex])
          );
        }
      );
      
      // Process results as they come in
      for await (const result of generator) {
        if (result && result.description) {
          const { slideNumber, description } = result;
          
          // Update descriptions immediately as they complete
          setGeneratedDescriptions(prev => ({
            ...prev,
            [slideNumber]: description
          }));
          
          // Set this slide to editing mode
          setEditingSlideIndices(prev => {
            const newSet = new Set(prev);
            newSet.add(slideNumber);
            return newSet;
          });
          
          setEditedDescriptions(prev => ({
            ...prev,
            [slideNumber]: description
          }));
          
          // Remove from loading state
          setGeneratingDescriptions(prev => 
            prev.filter(num => num !== slideNumber)
          );
        }
      }
    } catch (err) {
      console.error('Failed to generate descriptions:', err);
    } finally {
      // Ensure we clear the generating state when done
      setTimeout(() => {
        setGeneratingDescriptions([]);
      }, 500);
    }
  };

  const handleAcceptAllGeneratedDescriptions = async () => {
    if (!currentFile || isReadOnlyMode) return;

    try {
      // Update all slide descriptions individually
      await Promise.all(
        Object.entries(generatedDescriptions).map(([slideNumber, description]) =>
          handleUpdateSlideDescription(parseInt(slideNumber), description)
        )
      );

      setGeneratedDescriptions({});
      setEditingSlideIndices(new Set());
      setEditedDescriptions({});
    } catch (err) {
      console.error('Failed to accept all descriptions:', err);
    }
  };

  const handleThumbnailUpload = async (slideNumber: number, file: File) => {
    if (!currentFile || isReadOnlyMode) return;
    
    try {
      setUploadingThumbnails(prev => [...prev, slideNumber]);
      await handleUploadThumbnail(slideNumber, file);
    } catch (err) {
      console.error('Failed to upload thumbnail:', err);
    } finally {
      setUploadingThumbnails(prev => prev.filter(num => num !== slideNumber));
    }
  };

  const handleThumbnailDelete = async (slideNumber: number) => {
    if (!currentFile || isReadOnlyMode) return;
    
    try {
      setUploadingThumbnails(prev => [...prev, slideNumber]);
      await handleDeleteThumbnail(slideNumber);
    } catch (err) {
      console.error('Failed to delete thumbnail:', err);
    } finally {
      setUploadingThumbnails(prev => prev.filter(num => num !== slideNumber));
    }
  };

  const handleFileInputChange = (slideNumber: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && !isReadOnlyMode) {
      handleThumbnailUpload(slideNumber, file);
    }
  };

  const handleGenerateFileDescription = async () => {
    if (!currentFile || isReadOnlyMode) return;
    
    try {
      setIsGeneratingFileDescription(true);
      const description = await handleGeneratePptxDescription({ lang });
      if (description) {
        setEditingFileDescription(true);
        setEditedFileDescription(description);
      }
    } catch (err) {
      console.error('Failed to generate file description:', err);
    } finally {
      setIsGeneratingFileDescription(false);
    }
  };

  const handleSaveFileDescription = async () => {
    if (!currentFile || isReadOnlyMode) return;
    
    try {
      await handleUpdatePptxFile({description: editedFileDescription});
      setEditingFileDescription(false);
    } catch (err) {
      console.error('Failed to save file description:', err);
    }
  };

  const handleToggleLock = async (slideNumber: number, elemIndex: number) => {
    if (isReadOnlyMode) return;
    
    const key = `${slideNumber}-${elemIndex}`;
    // Toggle the state optimistically
    const newLockedState = !lockedElements[key];
    setLockedElements(prev => ({
      ...prev,
      [key]: newLockedState
    }));

    try {
      // Save to backend using the API service
      if (currentFile) {
        await handleUpdateSlideElementLocked(slideNumber, elemIndex, newLockedState);
      }
    } catch (err) {
      // If there's an error, revert the state change
      setLockedElements(prev => ({
        ...prev,
        [key]: !newLockedState
      }));
      console.error('Error toggling element lock status:', err);
    }
  };

  // Debounce hover state changes slightly to avoid flickering if mouse moves quickly
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleSetHoveredElement = useCallback((element: { slideNumber: number; elemIndex: number } | null) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredElement(element);
    }, 50); // 50ms delay
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Handler for hover events originating from the preview component
  const handlePreviewElementHover = useCallback((slideNumber: number) => (elementIndex: number | null) => {
    handleSetHoveredElement(elementIndex !== null ? { slideNumber, elemIndex: elementIndex } : null);
  }, [handleSetHoveredElement]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !currentFile) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          {error ? `Error loading file: ${error}` : 'File not found'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Stack 
        direction="row" 
        alignItems="center" 
        justifyContent="flex-start" 
        mb={4} 
        mt={2}
      >
        <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" component="h1">
          {currentFile.filename}
        </Typography>
      </Stack>

      {isReadOnlyMode && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
        >
          {lang === 'en' 
            ? 'You are viewing a public template created by another user. Only read-only mode is available.'
            : '您正在查看其他用户创建的公共模板。仅支持只读模式。'
          }
        </Alert>
      )}

      <Box mb={2}>
        <Typography variant="h6" gutterBottom>
          {lang === 'en' ? 'File Description' : '文件描述'}
        </Typography>
        {editingFileDescription ? (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editedFileDescription}
              onChange={(e) => setEditedFileDescription(e.target.value)}
              label={lang === 'en' ? 'Description' : '描述'}
              variant="outlined"
              sx={{ mb: 2 }}
              disabled={isReadOnlyMode}
            />
            <Button
              variant="contained"
              onClick={handleSaveFileDescription}
              sx={{ mr: 1 }}
              disabled={isReadOnlyMode}
            >
              {lang === 'en' ? 'Save' : '保存'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setEditingFileDescription(false);
                setEditedFileDescription('');
              }}
              disabled={isReadOnlyMode}
            >
              {lang === 'en' ? 'Cancel' : '取消'}
            </Button>
          </Box>
        ) : (
          <Box>
            {!currentFile.description ? (
              <Alert 
                severity="warning" 
                sx={{ mb: 2 }}
              >
                {lang === 'en' ? 'This PPTX file has no description' : '此PPTX文件缺少描述。添加描述信息以便AI根据创作需求选择此模板。'}
              </Alert>
            ) : (
              <Typography variant="subtitle1" gutterBottom>
                {currentFile.description}
              </Typography>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={isGeneratingFileDescription || isReadOnlyMode}
                onClick={() => {
                  setEditingFileDescription(true);
                  setEditedFileDescription(currentFile?.description || '');
                }}
              >
                {lang === 'en' ? 'Edit Description' : '编辑描述'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleGenerateFileDescription}
                disabled={isGeneratingFileDescription || isReadOnlyMode}
                startIcon={isGeneratingFileDescription ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
              >
                {isGeneratingFileDescription ? (lang === 'en' ? 'Generating...' : '生成描述') : (lang === 'en' ? 'Generate Description' : '生成描述')}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {currentFile.parsedSlides.some(slide => !slide.description) && !isReadOnlyMode && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleGenerateAllMissingDescriptions}
              disabled={generatingDescriptions.length > 0}
              startIcon={generatingDescriptions.length > 0 ? <CircularProgress size={20} /> : null}
            >
              {generatingDescriptions.length > 0 ? (lang === 'en' ? 'Generating...' : '生成中...') : (lang === 'en' ? 'Generate All Missing' : '生成所有缺失的描述')}
            </Button>
          }
        >
          {lang === 'en' ? 'Some slides are missing descriptions' : '一些幻灯片缺少描述'}
        </Alert>
      )}

      {Object.keys(generatedDescriptions).length > 1 && !isReadOnlyMode && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={handleAcceptAllGeneratedDescriptions}
            >
              {lang === 'en' ? 'Accept All Generated Descriptions' : '接受所有生成的描述'}
            </Button>
          }
        >
          {lang === 'en' ? `${Object.keys(generatedDescriptions).length} descriptions have been generated` : `${Object.keys(generatedDescriptions).length} 个描述已生成`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {currentFile.parsedSlides.map((slide, index) => {
          const slideNumber = index + 1;
          return (
            <Grid item xs={12} key={slideNumber}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <SlidePreviewWithBoundaries
                        fileId={currentFile._id}
                        slideNumber={slideNumber}
                        slide={{
                          ...slide,
                          elements: slide.elements.map((elem, elemIndex) => ({
                            ...elem,
                            isLocked: lockedElements[`${slideNumber}-${elemIndex}`]
                          }))
                        }}
                        slideWidth={currentFile.slideWidth ? currentFile.slideWidth * 914400 : undefined}
                        slideHeight={currentFile.slideHeight ? currentFile.slideHeight * 914400 : undefined}
                        isLoading={uploadingThumbnails.includes(slideNumber)}
                        hoveredElementId={hoveredElement?.slideNumber === slideNumber ? String(hoveredElement.elemIndex) : undefined}
                        onElementHover={handlePreviewElementHover(slideNumber)}
                      />
                      {!isReadOnlyMode && (
                        <Stack direction="row" spacing={1} mt={1}>
                          <input
                            type="file"
                            accept="image/jpeg"
                            style={{ display: 'none' }}
                            ref={el => fileInputRefs.current[slideNumber] = el}
                            onChange={handleFileInputChange(slideNumber)} />
                          <Button
                            variant="outlined"
                            startIcon={<UploadIcon />}
                            onClick={() => fileInputRefs.current[slideNumber]?.click()}
                            disabled={uploadingThumbnails.includes(slideNumber)}
                          >
                            {lang === 'en' ? 'Upload' : '上传'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleThumbnailDelete(slideNumber)}
                            disabled={uploadingThumbnails.includes(slideNumber)}
                          >
                            {lang === 'en' ? 'Reset' : '重置'}
                          </Button>
                        </Stack>
                      )}
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="h6" gutterBottom>
                        {lang === 'en' ? `Slide ${slideNumber}` : `幻灯片 ${slideNumber}`}
                      </Typography>

                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{lang === 'en' ? 'Type' : '类型'}</TableCell>
                              <TableCell>{lang === 'en' ? 'Name' : '名称'}</TableCell>
                              <TableCell>{lang === 'en' ? 'Text' : '文本'}</TableCell>
                              <TableCell align="center">{lang === 'en' ? 'Locked' : '锁定'}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody
                            onMouseLeave={() => handleSetHoveredElement(null)}
                          >
                            {slide.elements.map((element, elemIndex) => {
                              const isHovered = hoveredElement?.slideNumber === slideNumber && hoveredElement?.elemIndex === elemIndex;
                              return (
                                <TableRow
                                  key={elemIndex}
                                  onMouseEnter={() => handleSetHoveredElement({ slideNumber, elemIndex })}
                                  sx={{
                                    backgroundColor: isHovered ? 'action.hover' : 'transparent',
                                    transition: 'background-color 0.1s ease-in-out',
                                  }}
                                >
                                  <TableCell>{element.type}</TableCell>
                                  <TableCell>{element.name}</TableCell>
                                  <TableCell>{element.innerText || '-'}</TableCell>
                                  <TableCell align="center">
                                    <Tooltip title={lockedElements[`${slideNumber}-${elemIndex}`] ? (lang === 'en' ? 'Locked' : '已锁定') : (lang === 'en' ? 'Unlocked' : '未锁定')}>
                                      <Switch
                                        size="small"
                                        checked={lockedElements[`${slideNumber}-${elemIndex}`] === true}
                                        onChange={() => handleToggleLock(slideNumber, elemIndex)}
                                        disabled={isReadOnlyMode}
                                      />
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {slide.elements.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="center">{lang === 'en' ? 'No elements found' : '没有找到元素'}</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box mt={2}>
                        {editingSlideIndices.has(slideNumber) ? (
                          <Box>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={editedDescriptions[slideNumber] || ''}
                              onChange={e => setEditedDescriptions(prev => ({
                                ...prev,
                                [slideNumber]: e.target.value
                              }))}
                              label="Description"
                              variant="outlined"
                              sx={{ mb: 2 }}
                              disabled={isReadOnlyMode}
                            />
                            <Button
                              variant="contained"
                              onClick={() => handleSaveDescription(slideNumber)}
                              sx={{ mr: 1 }}
                              disabled={isReadOnlyMode}
                            >
                              {lang === 'en' ? 'Save' : '保存'}
                            </Button>
                            <Button 
                              variant="outlined" 
                              onClick={() => handleCancelEdit(slideNumber)}
                              disabled={isReadOnlyMode}
                            >
                              {lang === 'en' ? 'Cancel' : '取消'}
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="subtitle1" gutterBottom>
                              {lang === 'en' ? 'Description:' : '描述:'}
                            </Typography>
                            <Typography paragraph>
                              {slide.description || (lang === 'en' ? 'No description' : '没有描述')}
                            </Typography>
                            {!isReadOnlyMode && (
                              <Stack direction="row" spacing={2}>
                                <Button
                                  variant="outlined"
                                  onClick={() => {
                                    handleEditDescription(
                                      slideNumber,
                                      generatedDescriptions[slideNumber] || slide.description || ''
                                    );
                                  }}
                                >
                                  {lang === 'en' ? 'Edit Description' : '编辑描述'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={() => handleGenerateDescription(slideNumber)}
                                  disabled={generatingDescriptions.includes(slideNumber)}
                                  startIcon={generatingDescriptions.includes(slideNumber) ? <CircularProgress size={20} /> : null}
                                >
                                  {generatingDescriptions.includes(slideNumber) ? (lang === 'en' ? 'Generating...' : '生成中...') : (lang === 'en' ? 'Generate Description' : '生成描述')}
                                </Button>
                              </Stack>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
}

export default PptxFileDetailPage;